import { useMemo, useState, Activity, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IconBook,
  IconUser,
  IconNotebook,
  IconProgressCheck,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ClassSummaryCard from '@/components/ClassSummaryCard';
import CustomImage from '@/components/CustomImage';
import EmptyState from '@/components/EmptyState';
import ProgressBar from '@/components/ProgressBar';
import TeacherDashboardSkeleton from '@/components/SkeletonLoader/TeacherDashboardSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getTeacherClassList } from '@/service/classService';
import {
  getTeacherDashboardSummary,
  getTeacherDashboardStudents,
} from '@/service/dashboardService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { ITeacherClass } from '@/types/class';
import {
  ITeacherDashboardSummary,
  ITeacherDashboardStudent,
} from '@/types/dashboard';
import { getAssetUrl, formatLastActive } from '@/utils';

import { StudentDetailDrawer } from '../Classes/components/StudentDetailDrawer';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const { formatText } = useFormatText();
  const { isScreenLoading, setScreenLoading, isTableLoading, setTableLoading } =
    useSkeletonStore();

  const [summaryData, setSummaryData] =
    useState<ITeacherDashboardSummary | null>(null);
  const [assignedClasses, setAssignedClasses] = useState<ITeacherClass[]>([]);
  const [totalClasses, setTotalClasses] = useState(0);
  const [studentsData, setStudentsData] = useState<ITeacherDashboardStudent[]>(
    []
  );
  const [totalStudents, setTotalStudents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'' | 'ASC' | 'DESC'>('');
  const [selectedStudent, setSelectedStudent] =
    useState<ITeacherDashboardStudent | null>(null);
  const [isStudentDrawerOpen, setIsStudentDrawerOpen] = useState(false);

  const pageTitle = formatText('dashboard.title');

  useEffect(() => {
    setTitle(pageTitle);
    return () => {
      setTitle('');
    };
  }, [pageTitle, setTitle]);

  const fetchDashboardData = async () => {
    try {
      setScreenLoading(true);
      const [summaryRes, classesRes] = await Promise.all([
        getTeacherDashboardSummary(),
        getTeacherClassList({ limit: 4, offset: 0 }),
      ]);

      if (summaryRes?.status) {
        setSummaryData(summaryRes.data);
      }
      if (classesRes?.status) {
        setAssignedClasses(classesRes.data || []);
        setTotalClasses(classesRes.total || 0);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setScreenLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setTableLoading(true);
      const response = await getTeacherDashboardStudents({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...(sortField && { [`order[${sortField}]`]: sortDirection }),
      });

      if (response?.status) {
        setStudentsData(response.data || []);
        setTotalStudents(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching dashboard students:', error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortField, sortDirection]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const summaryCards = useMemo(() => {
    return [
      {
        key: 'total-classes',
        label: formatText('dashboard.teacher-dashboard.total-classes'),
        value: summaryData?.total_classes || 0,
        icon: <IconNotebook color="#717784" size={24} />,
        dataTestId: 'total-classes',
      },
      {
        key: 'lessons-created',
        label: formatText('dashboard.teacher-dashboard.lessons-created'),
        value: summaryData?.lessons_created || 0,
        icon: <IconBook color="#717784" size={24} />,
        dataTestId: 'lessons-created',
      },
      {
        key: 'lesson-completed',
        label: formatText('dashboard.teacher-dashboard.lessons-completed'),
        value: summaryData?.lessons_completed || 0,
        icon: <IconProgressCheck color="#717784" size={24} />,
        dataTestId: 'lessons-completed',
      },
      {
        key: 'active-students',
        label: formatText('dashboard.teacher-dashboard.active-students'),
        value: summaryData
          ? `${summaryData.students.active_students}/${summaryData.students.total_students}`
          : '0/0',
        icon: <IconUser color="#717784" size={24} />,
        dataTestId: 'active-students',
      },
    ];
  }, [summaryData, formatText]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: formatText('dashboard.teacher-dashboard.columns.student-name'),
        sorter: true,
        handleSort: () => handleSort('name'),
        width: '200px',
        render: (record: ITeacherDashboardStudent) => (
          <div className="flex items-center gap-2">
            <AvatarGroupList
              list={[
                {
                  id: record?.id,
                  name: record?.name,
                  image:
                    getAssetUrl(record?.asset) ||
                    getAssetUrl(record?.profile_pic),
                },
              ]}
              size={40}
              avatarClassName="w-10 h-10 text-xs"
            />
            <span className="text-sm font-medium text-neutral-primary">
              {record.name}
            </span>
          </div>
        ),
      },
      {
        key: 'class_name',
        title: formatText('dashboard.teacher-dashboard.columns.class-name'),
        sorter: true,
        handleSort: () => handleSort('class_name'),
        width: '200px',
        render: (record: ITeacherDashboardStudent) => record.class_name,
      },
      {
        key: 'current_lesson_name',
        title: formatText('dashboard.teacher-dashboard.columns.current-lesson'),
        sorter: true,
        handleSort: () => handleSort('current_lesson_name'),
        width: '200px',
        render: (record: ITeacherDashboardStudent) =>
          record.current_lesson_name ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E0F2FE] flex items-center justify-center overflow-hidden shrink-0 border border-[#B9E6FE]">
                <CustomImage
                  src={record.current_lesson_img || '/assets/images/lesson.jpg'}
                  alt={record.current_lesson_name || ''}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-neutral-primary text-sm line-clamp-1">
                {record.current_lesson_name || '-'}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        key: 'progress',
        title: formatText('dashboard.teacher-dashboard.columns.progress'),
        sorter: true,
        handleSort: () => handleSort('progress'),
        width: '150px',
        render: (record: ITeacherDashboardStudent) => (
          <ProgressBar
            value={record.progress || 0}
            showPercentage={true}
            height="h-2"
            className="w-32"
          />
        ),
      },
      {
        key: 'status',
        title: formatText('dashboard.teacher-dashboard.columns.status'),
        sorter: true,
        handleSort: () => handleSort('status'),
        width: '150px',
        render: (record: ITeacherDashboardStudent) => {
          const config = LessonStatusConfig[record.status];

          return (
            <TagCapsule
              value={formatText(config?.label ?? '')}
              className={config?.className ?? ''}
            />
          );
        },
      },
      {
        key: 'last_active',
        title: formatText('dashboard.teacher-dashboard.columns.last-active'),
        sorter: true,
        handleSort: () => handleSort('last_active'),
        width: '150px',
        render: (record: ITeacherDashboardStudent) => (
          <span className="text-neutral-secondary text-sm">
            {formatLastActive(Number(record.last_active || 0), formatText)}
          </span>
        ),
      },
    ],
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDirection, formatText]
  );

  const handleRowViewClick = (rowId: string) => {
    const student = studentsData.find((s) => s.id === rowId) || null;
    setSelectedStudent(student);
    setIsStudentDrawerOpen(true);
  };

  const isDashboardEmpty =
    !isScreenLoading &&
    !isTableLoading &&
    (!summaryData || summaryData?.total_classes === 0) &&
    assignedClasses.length === 0 &&
    studentsData.length === 0;

  if (isDashboardEmpty) {
    return (
      <EmptyState
        imageSrc="/not-found.svg"
        imageAlt={formatText('dashboard.empty-title-teacher')}
        title={formatText('dashboard.empty-title-teacher')}
        description={formatText('dashboard.empty-description-teacher')}
        className="h-full!"
        dataTestId="dashboard-empty-state"
      />
    );
  }

  if (isScreenLoading) {
    return <TeacherDashboardSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-8" data-testid="teacher-dashboard">
      {/* Summary Cards */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="dashboard-summary-cards"
      >
        {summaryCards?.map((stat) => (
          <SummaryCard
            key={stat?.key}
            variant="dashboard"
            label={stat?.label}
            value={stat?.value}
            icon={stat?.icon}
            dataTestId={stat?.dataTestId}
            className="bg-white border-neutral-200"
          />
        ))}
      </div>

      {/* Assigned Classes */}
      <div
        className="flex flex-col gap-4"
        data-testid="assigned-classes-section"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-primary ps-1">
            {formatText('dashboard.teacher-dashboard.assigned-classes')}
          </h2>
          {totalClasses > 4 && (
            <Button
              variant="outline"
              label={formatText('dashboard.teacher-dashboard.view-all')}
              onClick={() => navigate('/teacher/dashboard/assigned-classes')}
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-md:gap-3">
          {assignedClasses.map((cls, index) => (
            <ClassSummaryCard
              key={cls.id}
              name={cls.name}
              studentsCount={cls.active_students}
              lessonsCount={cls.lesson_created}
              colorIndex={index}
              className="cursor-pointer hover:shadow-sm max-w-full!"
              onClick={() => navigate(`/teacher/classes/${cls.id}`)}
            />
          ))}
        </div>
      </div>

      {/* Student Progress Overview */}
      <div
        className="flex flex-col gap-4"
        data-testid="student-progress-section"
      >
        <h2 className="text-lg font-semibold text-neutral-primary ps-1">
          {formatText('dashboard.teacher-dashboard.student-progress-overview')}
        </h2>
        <CustomTable
          isLoading={isTableLoading}
          columns={columns}
          dataSource={studentsData}
          total={totalStudents}
          pageSize={pageSize}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setCurrentPage(1);
          }}
          fieldName={sortField}
          sortDirection={sortDirection}
          onRowClick={handleRowViewClick}
        />
      </div>

      <Activity
        mode={isStudentDrawerOpen && selectedStudent ? 'visible' : 'hidden'}
      >
        <StudentDetailDrawer
          isOpen={isStudentDrawerOpen}
          onClose={() => {
            setIsStudentDrawerOpen(false);
            setSelectedStudent(null);
          }}
          studentId={selectedStudent?.id || null}
          classId={selectedStudent?.class_id || ''}
        />
      </Activity>
    </div>
  );
};

export default TeacherDashboard;
